import { useMemo, useState } from "react";
import { Badge, Button, EmptyState, QueryStateBoundary, TabList, Tabs, TabTrigger } from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import { currentMonth, formatMonthLabel, getMonthGridDays, shiftMonth } from "@/features/events/eventDate";
import { useBookings, useResources } from "@/api/hooks/useBookings";
import { BOOKING_STATUS_BADGE_VARIANT, BOOKING_STATUS_LABELS, type Booking } from "@/api/types/booking";
import { CreateBookingDialog } from "./CreateBookingDialog";
import { BookingDetailDialog } from "./BookingDetailDialog";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Bookings — calendar/list view of room bookings by resource + date, with
 * create + detail/status-transition/generate-invoice actions. Reuses the
 * same month-grid helpers (features/events/eventDate.ts) as
 * features/calendar/CalendarPage.tsx.
 */
export function BookingsPage() {
  const { data: resources, isLoading: resourcesLoading, error: resourcesError } = useResources();
  const [resourceId, setResourceId] = useState<string>("");
  const [view, setView] = useState<"calendar" | "list">("calendar");
  const [month, setMonth] = useState(currentMonth());
  const [createOpen, setCreateOpen] = useState(false);
  const [createDate, setCreateDate] = useState<string | undefined>(undefined);
  const [detailBookingId, setDetailBookingId] = useState<string | null>(null);

  const activeResourceId = resourceId || resources?.[0]?.id || "";

  const days = useMemo(() => getMonthGridDays(month), [month]);
  const dateFrom = days[0]?.iso;
  const dateTo = days[days.length - 1]?.iso;

  const { data, isLoading, error, refetch } = useBookings({
    resource_id: activeResourceId || undefined,
    date_from: dateFrom,
    date_to: dateTo,
    limit: 100,
  });
  const bookings = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  const bookingsByDay = useMemo(() => {
    const map = new Map<string, Booking[]>();
    for (const b of bookings) {
      if (b.status === "cancelled") continue;
      const list = map.get(b.booking_date) ?? [];
      list.push(b);
      map.set(b.booking_date, list);
    }
    return map;
  }, [bookings]);

  function openCreate(dateIso?: string) {
    setCreateDate(dateIso);
    setCreateOpen(true);
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Bookings</h1>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setView((v) => (v === "calendar" ? "list" : "calendar"))}
          >
            {view === "calendar" ? "List view" : "Calendar view"}
          </Button>
          <Button size="sm" onClick={() => openCreate()} data-testid="new-booking-button">
            New booking
          </Button>
        </div>
      </div>

      <QueryStateBoundary isLoading={resourcesLoading} error={resourcesError}>
        {resources && resources.length > 0 ? (
          <Tabs value={activeResourceId} onValueChange={setResourceId}>
            <TabList className="mb-4">
              {resources.map((r) => (
                <TabTrigger key={r.id} value={r.id}>
                  {r.name}
                </TabTrigger>
              ))}
            </TabList>
          </Tabs>
        ) : (
          <EmptyState title="No resources configured" description="Seed resources via scripts/seed_resources.py." />
        )}
      </QueryStateBoundary>

      {view === "calendar" ? (
        <>
          <div className="mb-4 flex items-center justify-center gap-4">
            <Button size="sm" variant="secondary" onClick={() => setMonth((m) => shiftMonth(m, -1))} aria-label="Previous month">
              ← Prev
            </Button>
            <h2 className="min-w-[10rem] text-center text-lg font-semibold text-text-primary" data-testid="bookings-month-label">
              {formatMonthLabel(month)}
            </h2>
            <Button size="sm" variant="secondary" onClick={() => setMonth((m) => shiftMonth(m, 1))} aria-label="Next month">
              Next →
            </Button>
          </div>

          <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
            <div className="overflow-x-auto">
              <div className="grid min-w-[640px] grid-cols-7 gap-px rounded-lg border border-border bg-border">
                {WEEKDAY_LABELS.map((label) => (
                  <div key={label} className="bg-bg-hover px-2 py-1 text-center text-xs font-semibold uppercase text-text-muted">
                    {label}
                  </div>
                ))}
                {days.map((day) => {
                  const dayBookings = bookingsByDay.get(day.iso) ?? [];
                  return (
                    <div
                      key={day.iso}
                      data-day={day.iso}
                      onClick={() => openCreate(day.iso)}
                      className={`min-h-[6rem] cursor-pointer bg-bg-card p-1.5 hover:bg-bg-hover ${day.inMonth ? "" : "opacity-40"} ${
                        day.isToday ? "ring-2 ring-inset ring-primary" : ""
                      }`}
                    >
                      <div className="mb-1 text-xs font-medium text-text-secondary">{day.date.getDate()}</div>
                      <div className="space-y-1">
                        {dayBookings.map((b) => (
                          <button
                            key={b.id}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDetailBookingId(b.id);
                            }}
                            className="block w-full truncate rounded px-1 py-0.5 text-left text-xs hover:bg-bg-hover"
                            data-booking-chip={b.id}
                          >
                            <Badge variant={BOOKING_STATUS_BADGE_VARIANT[b.status]} size="sm" className="mr-1">
                              {BOOKING_STATUS_LABELS[b.status]}
                            </Badge>
                            {b.is_shadow ? "Whole House" : b.slot_label ?? "Full day"}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </QueryStateBoundary>
        </>
      ) : (
        <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
          {bookings.length === 0 ? (
            <EmptyState
              title="No bookings this month"
              description="Create a booking to get started."
              action={{ label: "New booking", onClick: () => openCreate() }}
            />
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border bg-bg-card" data-testid="bookings-list">
              {bookings
                .filter((b) => b.status !== "cancelled")
                .sort((a, b) => a.booking_date.localeCompare(b.booking_date))
                .map((b) => (
                  <li key={b.id}>
                    <button
                      type="button"
                      onClick={() => setDetailBookingId(b.id)}
                      className="flex w-full flex-col gap-1 px-4 py-3 text-left hover:bg-bg-hover sm:flex-row sm:items-center sm:justify-between"
                      data-booking-row={b.id}
                    >
                      <div>
                        <p className="font-medium text-text-primary">
                          {b.resource_name}
                          {b.is_shadow && <span className="ml-2 text-xs text-text-muted">(Whole House block)</span>}
                        </p>
                        <p className="text-xs text-text-muted">
                          {b.booking_date} · {b.slot_label ?? "Full day"}
                          {b.extra_hour ? " (+1 hr)" : ""}
                          {b.occasion ? ` · ${b.occasion}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-text-secondary">{centsToUsd(b.price_cents)}</span>
                        <Badge variant={BOOKING_STATUS_BADGE_VARIANT[b.status]}>{BOOKING_STATUS_LABELS[b.status]}</Badge>
                      </div>
                    </button>
                  </li>
                ))}
            </ul>
          )}
        </QueryStateBoundary>
      )}

      <CreateBookingDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultResourceId={activeResourceId}
        defaultDate={createDate}
      />
      <BookingDetailDialog bookingId={detailBookingId} onClose={() => setDetailBookingId(null)} />
    </div>
  );
}
